<template>
    <div class="right-part mail-compose bg-white">
        <div class="p-20 border-bottom">
            <div class="d-flex align-items-center">
                <div>
                    <h4>{{compose.subject || 'Compose Message'}}</h4>
                    <span>Type the first few characters to fetch recipient names</span>
                </div>
                <div class="ml-auto">
                    <button id="cancel_compose" class="btn btn-dark" @click="goBack()">Back</button>
                </div>
            </div>
        </div>
        <div class="card-body border-bottom">
            <form @submit.prevent="handleSubmit">
                <div class="form-group">
                    <multiselect
                            id="ajax"
                            name="recipients"
                            label="name"
                            class="form-control"
                            track-by="id"
                            placeholder="Type to search"
                            open-direction="bottom"
                            v-model="recipients"
                            :options="users"
                            :multiple="true"
                            :searchable="true"
                            :loading="isLoading"
                            :internal-search="false"
                            :clear-on-select="false"
                            :close-on-select="false"
                            :options-limit="300"
                            :limit="5"
                            :limit-text="limitText"
                            :max-height="600"
                            :show-no-results="false"
                            :hide-selected="true"
                            @search-change="getUsers"
                            v-validate="'required'"
                            :class="{'is-invalid': submitted && errors.has('recipients')}">
                        <template slot="tag" slot-scope="{ option, remove }">
                            <span class="recipient-tag">
                                <span>{{ `${option.first_name}  ${option.last_name} - ${option.org_name}` }}</span>
                                <span class="recipient-remove" @click="remove(option)">❌</span>
                            </span>
                        </template>
                        <template slot="clear" slot-scope="props">
                            <div class="recipient-clear" v-if="recipients.length" @mousedown.prevent.stop="clearAll(props.search)"></div>
                        </template>
                        <template slot="option" slot-scope="props">
                            <h5>{{props.option.first_name}} {{props.option.last_name}}</h5>
                            <span>{{props.option.org_name}}</span>
                        </template>
                        <span slot="noResult">Oops! No users found. Consider changing the search query.</span>
                    </multiselect>
                    <div v-if="submitted && errors.has('recipients')" class="invalid-feedback">{{ errors.first('recipients') }}</div>
                </div>
                <div class="form-group">
                    <input type="text" name="subject"
                           class="form-control"
                           placeholder="Subject"
                           v-model="compose.subject"
                           v-validate="'required'"
                           :class="{'is-invalid': submitted && errors.has('subject')}">
                    <div v-if="submitted && errors.has('subject')" class="invalid-feedback">{{ errors.first('subject') }}</div>
                </div>
                <div class="form-group">
                    <textarea
                            class="form-control"
                            rows="10"
                            placeholder="Your message here..."
                            name="message"
                            v-model="compose.message"
                            v-validate="'required'"
                            :class="{'is-invalid': submitted && errors.has('message')}"></textarea>
                    <div v-if="submitted && errors.has('message')" class="invalid-feedback">{{ errors.first('message') }}</div>
                </div>
                <div class="card-body">
                    <h4 class="card-title">Attachments <span v-if="compose.attachments.length > 0">({{compose.attachments.length}})</span></h4>
                    <span class="card-subtitle">Max file size of 5MB</span>
                    <form>
                        <div class="input-group mb-3">
                            <div class="input-group-prepend">
                                <span class="input-group-text">Upload</span>
                            </div>
                            <div class="custom-file">
                                <input type="file" id="file" ref="file" class="custom-file-input" v-on:change="onFileChange()" accept=".PDF,.JPG,.JPEG,.PNG">
                                <label class="custom-file-label">{{filename}}</label>
                            </div>
                        </div>
                        <div class="attachments">
                            <div v-for="(attachment, idx) in compose.attachments"><span href="#" v-on:click="getDownloadUrl(idx)">{{ attachment.filename }}</span><span
                                    @click="removeAttachment(idx)">❌</span></div>
                        </div>
                    </form>
                </div>
                <div class="card-body">
                    <div class="button-group text-right">
                        <button type="submit" class="btn btn-success m-t-20"><i class="far fa-envelope"></i> Send</button>
                        <button type="button" class="btn btn-inverse m-t-20" @click="saveDraft">Save Draft</button>
                        <button type="button" class="btn btn-inverse m-t-20" @click="discard">Discard</button>
                    </div>
                </div>
            </form>
        </div>

    </div>
</template>

<script>
    import axios from "axios";
    import _ from "lodash";
    import Multiselect from 'vue-multiselect';

    export default {
        name: "Compose",
        props: ["mailid"],
        components: {
            Multiselect
        },
        data() {
            return {
                draft_id: null,
                submitted: false,
                isLoading: false,
                users: [],
                recipients: [],
                lastSelectItem: {},
                searchText: '',
                file: File,
                filename: "No File Chosen",
                noOfAttachments: 0,
                compose: {
                    parentid: null,
                    subject: '',
                    message: '',
                    recipientids: [],
                    attachments: []
                }
            }
        },
        mounted() {
            if(this.mailid !== undefined) {
                this.draft_id = this.mailid;
                this.getDraft(this.draft_id);
            } else {
                this.createDraft();
            }
        },
        methods: {
            // Lookup recipients
            async getUsers(searchText) {
                this.isLoading = true;
                let reply      = await this.$http.get("/mailbox/users", {
                    params: {
                        search: searchText
                    }
                });
                this.users     = reply.data;
                this.isLoading = false;
            },
            clearAll() {
                this.recipients = [];
            },
            limitText(count) {
                return `and ${count} more users.`
            },

            // Drafts

            async createDraft() {
                let draft_id  = await this.$http.put('/mailbox', {});
                this.draft_id = draft_id.data.mailid;
            },
            async saveDraft() {
                if(this.draft_id === null) {
                    await this.createDraft();
                }
                await this.$http.post(`/mailbox/${this.draft_id}`, this.compose);
                this.$toastr('success', 'Message saved as draft successfully.', 'Draft Saved');
            },
            async getDraft(mailid) {
                try {
                    let draft    = await this.$http.get("/mailbox/" + mailid);
                    this.compose = draft.data;
                } catch(error) {
                    // this.$router.push("/mailbox/compose");
                    this.createDraft();
                }
            },

            // Atttachments

            async onFileChange() {
                this.file     = this.$refs.file.files[0];
                this.filename = this.file.name;
                if(this.draft_id === null) {
                    await this.createDraft();
                }
                let signedurl = await this.getUploadUrl(this.draft_id, this.filename);
                let uploaded  = await this.uploadFile(signedurl.data.url, signedurl.data.key, this.file.type);
                this.compose.attachments.push({
                    filename: this.filename,
                    filepath: signedurl.data.key,
                    contenttype: this.file.type.split('/')[1]
                });
                this.$refs.file.value = '';
                this.filename         = "Click here to add more";
            },
            async getUploadUrl(mailId, filename) {
                let signedUrl = await this.$http.get(`/mailbox/${mailId}/signedUrl`, {
                    params: {
                        name: filename,
                        type: 'write'
                    }
                });
                return signedUrl;
            },
            async getDownloadUrl(index) {
                let filepath = this.compose.attachments[index].filepath;
                let result   = await this.$http.get(`/mailbox/${this.draft_id}/signedUrl`, {
                    params: {
                        type: 'read',
                        key: filepath
                    }
                });

                window.location.href = result.data.url;
            },
            async removeAttachment(index) {
                this.compose.attachments.splice(index, 1);
                if(this.compose.attachments.length > 0) {
                    this.filename = "Click here to add more";
                } else {
                    this.filename = "No File Chosen";
                }
            },
            async uploadFile(signedUrl, filepath, contenttype) {
                await axios.put(signedUrl, this.file);
            },

            // Form actions

            handleSubmit(e) {
                this.submitted = true;
                this.$validator.validate()
                .then(valid => {
                    if(valid) {
                        this.send();
                    }
                });
            },
            async send() {
                let params = {
                    ...this.compose,
                    recipientids: this.recipients.map(r => r.id)
                };
                await this.$http.post(`/mailbox/${this.draft_id}/send`, params);
                this.$router.push("/mailbox");
            },
            async discard() {
                await this.$http.delete(`/mailbox/${this.draft_id}`);
                // this.$router.push("/mailbox");
                await this.goBack();
            },
            goBack() {
                window.history.back();
            }
        }
    }
</script>

<style scoped>
    .recipient-tag {
        display: inline-block;
        padding: 3px 12px;
        background: #6ab43e;
        margin-right: 8px;
        margin-bottom: 8px;
        border-radius: 10px;
        cursor: pointer;
        color: #fff;
        text-transform: none;
        font-weight: bold;
    }

    .recipient-remove {
        padding: 0;
        font-size: 10px;
        margin-left: 5px;
    }

    .recipient-clear {
        position: absolute;
        right: 41px;
        height: 40px;
        width: 40px;
        display: block;
        cursor: pointer;
        z-index: 2;
    }

    .recipient-clear:after, .recipient-clear:before {
        content: "❌";
        display: block;
        position: absolute;
        top: 12px;
        right: 4px;
        font-weight: bold;
        font-size: 16px;
    }

    .attachments > div {
        margin: 15px 15px 0 0;
        padding: 5px 12px 5px 15px;
        border: #0c5460 solid 2px;
        border-radius: 50px;
        color: #3e5569;
        display: inline;
    }

    .attachments > div > span {
        margin-left: 7pX;
        cursor: pointer;
    }
</style>
